import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import {
  createEmptySurvey,
  sanitizeSurveyQuestions,
  type Survey,
  type SurveyExport,
  type SurveyQuestion,
  type SurveyStatus,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * アンケート管理 hook (admin 第 22 弾、Phase 5 A-S13 / A-S14)。
 *
 * 旧 `old/apps/admin/src/lib/surveys.ts` の getDocs ベース CRUD を v2 では onSnapshot で reactive 化
 * しつつ、create/update/setStatus の 3 関数で minimum scope の admin 操作を提供 (削除 / duplicate /
 * master 連携 / responses 出力は別 sprint)。
 *
 * 認可: firestore.rules `isDistrictAdmin(districtId)` → master / district admin (担当地区) のみ
 * write 可能。物理 delete は rules で禁止 (responseCount / 回答履歴を守るため)。
 *
 * 旧との差分:
 *   - 旧 getDocs (one-shot 取得 + 手動 reload) → onSnapshot (subscribe で reactive)
 *   - 旧 saveSurvey (`setDoc({ merge: false })` で全置換) → 本 sprint では create と update を分離
 *     (create は addDoc で auto-ID、update は merge:true で patch のみ)
 *   - master 連携 / duplicate / JSON export / CSV builder は scope 外 (別 sprint で復活)
 */

/** subscribe state。 */
export type SurveysState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: SurveyListItem[]; error: null }
  | { status: 'error'; items: []; error: Error };

/** 一覧表示用 narrow 型。Survey をそのまま使うと FieldValue 等が混じるのでこちらに正規化。 */
export type SurveyListItem = Survey;

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asStatus(value: unknown): SurveyStatus {
  return value === 'published' || value === 'closed' ? value : 'draft';
}

function asQuestions(value: unknown): SurveyQuestion[] {
  if (!Array.isArray(value)) return [];
  // 旧 schema で 10 種類サポート、本 hook は型 narrow のみ (詳細 validation は別 sprint)。
  return value.filter(
    (q): q is SurveyQuestion =>
      typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
  );
}

/**
 * Firestore doc → Survey & { id }。
 * 不正値 (status 不正・questions undefined 等) は default fallback。
 * 旧 `normalizeSurvey` を踏襲しつつ、master 連携メタは presence-aware に保持。
 */
export function mapSurvey(snap: { id: string; data: () => DocumentData }): SurveyListItem {
  const data = snap.data();
  const out: SurveyListItem = {
    id: snap.id,
    title: typeof data.title === 'string' ? data.title : '',
    description: typeof data.description === 'string' ? data.description : '',
    status: asStatus(data.status),
    questions: asQuestions(data.questions),
    collectParticipantProfile:
      typeof data.collectParticipantProfile === 'boolean' ? data.collectParticipantProfile : true,
    allowEdit: typeof data.allowEdit === 'boolean' ? data.allowEdit : true,
    schemaVersion: 1,
    responseCount: typeof data.responseCount === 'number' ? data.responseCount : 0,
    createdAt: tsToIso(data.createdAt),
    updatedAt: tsToIso(data.updatedAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
    version: typeof data.version === 'number' ? data.version : 0,
  };
  if (data.sourceMasterSurveyId !== undefined) {
    out.sourceMasterSurveyId =
      typeof data.sourceMasterSurveyId === 'string' ? data.sourceMasterSurveyId : null;
  }
  if (data.linkedToMaster !== undefined) {
    const v = data.linkedToMaster;
    if (v === 'linked' || v === 'forked' || v === 'local') out.linkedToMaster = v;
  }
  if (typeof data.masterVersion === 'number') out.masterVersion = data.masterVersion;
  return out;
}

export function useSurveys(districtId: string | null): SurveysState {
  const [state, setState] = useState<SurveysState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(
      collection(db, 'districts', districtId, 'surveys'),
      orderBy('updatedAt', 'desc'),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(mapSurvey);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);

  return state;
}

/**
 * 新規アンケート作成 (auto-ID)。返却は新規 ID。
 * payload は `createEmptySurvey` ベースだが createdAt/updatedAt は serverTimestamp に差し替え。
 */
export async function createSurvey(districtId: string, actorUid: string): Promise<string> {
  // id は addDoc 後に確定するが、createEmptySurvey に空 id を渡してから payload を組む。
  const template = createEmptySurvey('', actorUid);
  const ref = collection(db, 'districts', districtId, 'surveys');
  const created = await addDoc(ref, {
    schemaVersion: 1,
    title: template.title,
    description: template.description,
    status: template.status,
    questions: template.questions,
    collectParticipantProfile: template.collectParticipantProfile,
    allowEdit: template.allowEdit,
    responseCount: 0,
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actorUid,
    updatedBy: actorUid,
  });
  return created.id;
}

/** 編集 form の patch 入力 (admin が触れる field のみ)。 */
export interface UpdateSurveyInput {
  title?: string;
  description?: string;
  status?: SurveyStatus;
  questions?: SurveyQuestion[];
  collectParticipantProfile?: boolean;
  allowEdit?: boolean;
}

/**
 * patch を merge:true で書込。version は +1、updatedAt/updatedBy を server で上書き。
 * patch の項目は undefined なら書かない。questions の中の undefined (数値欄を空にした等) は
 * Firestore が保存全体をエラーにするため sanitizeSurveyQuestions で取り除いてから書く。
 */
export async function updateSurvey(
  districtId: string,
  surveyId: string,
  patch: UpdateSurveyInput,
  actorUid: string,
  currentVersion: number,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'surveys', surveyId);
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    updatedBy: actorUid,
    version: currentVersion + 1,
  };
  if (patch.title !== undefined) payload.title = patch.title;
  if (patch.description !== undefined) payload.description = patch.description;
  if (patch.status !== undefined) payload.status = patch.status;
  if (patch.questions !== undefined) payload.questions = sanitizeSurveyQuestions(patch.questions);
  if (patch.collectParticipantProfile !== undefined) {
    payload.collectParticipantProfile = patch.collectParticipantProfile;
  }
  if (patch.allowEdit !== undefined) payload.allowEdit = patch.allowEdit;
  await setDoc(ref, payload, { merge: true });
}

/**
 * Survey 複製 (Sprint BE / GAP-A9)。
 *
 * - title は元の title に '(コピー)' を追記
 * - status は 'draft' に強制 (公開状態は引き継がない)
 * - questions / collectParticipantProfile / allowEdit は元のものをそのまま copy
 * - responseCount は 0 にリセット (新規 survey 扱い)
 * - master 連携メタ (sourceMasterSurveyId / linkedToMaster / masterVersion) は引き継がない
 * - 旧 v1 `duplicateSurvey` を踏襲しつつ v2 では create のみ (rename / move 等は scope 外)
 */
export async function duplicateSurvey(
  districtId: string,
  source: SurveyListItem,
  actorUid: string,
): Promise<string> {
  const ref = collection(db, 'districts', districtId, 'surveys');
  const created = await addDoc(ref, {
    schemaVersion: 1,
    title: `${source.title || '(無題)'}(コピー)`,
    description: source.description,
    status: 'draft' as SurveyStatus,
    questions: source.questions,
    collectParticipantProfile: source.collectParticipantProfile,
    allowEdit: source.allowEdit,
    responseCount: 0,
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actorUid,
    updatedBy: actorUid,
  });
  return created.id;
}

/**
 * status のみ変更 (一覧画面の quick toggle 用)。
 * version は +1、updatedAt/updatedBy を server で上書き。
 */
export async function setSurveyStatus(
  districtId: string,
  surveyId: string,
  status: SurveyStatus,
  actorUid: string,
  currentVersion: number,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'surveys', surveyId);
  await setDoc(
    ref,
    {
      status,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
      version: currentVersion + 1,
    },
    { merge: true },
  );
}

/**
 * JSON export envelope から地区 survey を新規作成 (AW8、docs/108 High3)。
 *
 * 旧2 `SurveysPage.onImportFile` 踏襲: status は draft 強制、responseCount=0、version=1、
 * master 連携メタは引き継がない (別環境からの取込なので local 扱い)。
 * 戻り値は新規 surveyId。
 */
export async function importSurveyFromExport(
  districtId: string,
  exp: SurveyExport,
  actorUid: string,
): Promise<string> {
  const s = exp.survey;
  const ref = collection(db, 'districts', districtId, 'surveys');
  const created = await addDoc(ref, {
    schemaVersion: 1,
    title: typeof s.title === 'string' ? s.title : '(無題)',
    description: typeof s.description === 'string' ? s.description : '',
    status: 'draft' as SurveyStatus,
    questions: Array.isArray(s.questions) ? s.questions : [],
    collectParticipantProfile:
      typeof s.collectParticipantProfile === 'boolean' ? s.collectParticipantProfile : true,
    allowEdit: typeof s.allowEdit === 'boolean' ? s.allowEdit : true,
    responseCount: 0,
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actorUid,
    updatedBy: actorUid,
  });
  return created.id;
}

/**
 * linked アンケートを元 master preset の最新内容で上書き (AW8、docs/108 High2)。
 *
 * 旧2 `lib/surveys.ts::refreshLinkedSurvey` を v2 schema (master = surveyPresets) で踏襲。
 *   - source は `surveyPresets/{sourceMasterSurveyId}` (v2 では master は surveyPresets collection)
 *   - title/description/questions/collectParticipantProfile/allowEdit を preset 最新で上書き
 *   - status / responseCount は地区側の現状維持、version は +1、masterVersion を preset.version に更新
 *   - 地区での独自編集は失われる (旧2 と同じ destructive 仕様、UI 側で確認ダイアログ必須)
 */
export async function refreshLinkedSurvey(
  districtId: string,
  survey: Pick<
    Survey,
    'id' | 'status' | 'responseCount' | 'version' | 'linkedToMaster' | 'sourceMasterSurveyId'
  >,
  actorUid: string,
): Promise<void> {
  if (survey.linkedToMaster !== 'linked' || !survey.sourceMasterSurveyId) {
    throw new Error('リンク済みアンケートではありません');
  }
  const presetRef = doc(db, 'surveyPresets', survey.sourceMasterSurveyId);
  const presetSnap = await getDoc(presetRef);
  if (!presetSnap.exists()) {
    throw new Error('元のアンケートテンプレが見つかりません');
  }
  const preset = presetSnap.data();
  const presetTitle =
    typeof preset.title === 'string' && preset.title.trim()
      ? preset.title
      : typeof preset.displayName === 'string'
        ? preset.displayName
        : '(無題)';
  const ref = doc(db, 'districts', districtId, 'surveys', survey.id);
  await setDoc(
    ref,
    {
      title: presetTitle,
      description: typeof preset.description === 'string' ? preset.description : '',
      questions: Array.isArray(preset.questions) ? preset.questions : [],
      collectParticipantProfile:
        typeof preset.collectParticipantProfile === 'boolean'
          ? preset.collectParticipantProfile
          : true,
      allowEdit: typeof preset.allowEdit === 'boolean' ? preset.allowEdit : true,
      linkedToMaster: 'linked',
      sourceMasterSurveyId: survey.sourceMasterSurveyId,
      masterVersion: typeof preset.version === 'number' ? preset.version : 0,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
      version: (survey.version ?? 0) + 1,
    },
    { merge: true },
  );
}
